import * as actions from '../../../../../actions'
import Button from '@doordash/component-button'
import { EmailField, PhoneField } from '@doordash/component-fields'
import { Modal } from '@doordash/component-modal'
import { StackChildren, Text } from '@doordash/design-language'
import { isFeatureFlagEnabled } from '@doordash/lib-feature-flags'
import * as React from 'react'
import Form from '../../../../../utilities/Form'
import { pageLoad, Steps } from '../../../../../utilities/steps'
import { Error } from './styles'
import { connect } from 'react-redux'
import * as events from '../../../../../events'
import { validators } from '../../../../../utilities/validators'
import {
  AlreadyStartedEnum,
  AlreadyStartedSigningUpModalProps,
  SupportedCountryCode,
  EmploymentType,
} from '../../../../../models/types'
import * as DA from '../../../../../typeModules/events'
import { setApplicantStore } from '../../../../../utilities/application/application'
import routes from '../../../../../routes'
import t from './strings'
import {
  getOrderedPhoneCountryCodes,
  formatToE164,
} from '../../../../../utilities/phoneNumber'
import * as Exp from '../../../../../experimentConstants'
import { SmsSentPrompt } from './SmsSentPrompt'
import { SmsSentConfirmation } from './SmsSentConfirmation'
import { CountryCode } from '@doordash/lib-i18n'
import { shouldTriggerSms } from '../../../../../api/returnToApplication'
import SimpleStore from '../../../../../utilities/SimpleStore'
import { UNIQUE_LINK_KEY } from '../../../../../constants'
import { redirectToLogin } from '../../../../../utilities/returnToApp'

type Props = {
  onSubmit?: (any) => Promise<any>
  onClose: any
  applicantUniqueLink?: string
  trackDasherContinueApplication?: (
    data?: DA.DasherContinueApplication
  ) => Promise<any>
  alreadyStartedSigningUpModal?: AlreadyStartedSigningUpModalProps
  phoneCountry?: SupportedCountryCode
  includeAllCountryCodes?: boolean
  experiments?: any
  employmentType?: EmploymentType
  checkEmailIsAssociatedWithDasher?: (
    email: string
  ) => Promise<{
    shouldForwardToLogin: boolean
    applicantUniqueLink: string
  }>
  sendSmsRecovery: (phoneNumber: string) => Promise<boolean>
  trackReturnToApplication?: (
    action: DA.EventActions,
    name: DA.ReturnToApplication,
    value?: object
  ) => Promise<void>
}

export class StartedSigningUpModal extends React.Component<Props> {
  public static fields = {
    phoneNumber: '',
    email: '',
  }

  public static validators = {
    phoneNumber: validators.phoneNumber,
    email: validators.email,
  }

  public static defaultProps = {
    phoneCountry: 'US',
  }

  public state = {
    error: '',
    nationalPhoneNumber: '',
    phoneCountry: '',
    hasSentSms: false,
    isSmsSendInProgress: false,
    showSmsPromptModal: false,
    failedSubmitData: { phoneNumber: '' },
  }

  public componentDidMount() {
    const {
      trackDasherContinueApplication,
      alreadyStartedSigningUpModal,
    } = this.props

    trackDasherContinueApplication({
      action: DA.EventActions.Rendered,
      name: alreadyStartedSigningUpModal.type,
    })
  }

  private handleError = error => {
    if (typeof error === 'string') {
      return this.setState({ error })
    }
    throw error
  }

  private closeSmsModal = () => {
    this.setState({ showSmsPromptModal: false })
    this.props.onClose()
  }

  private handleSendSmsRecovery = async () => {
    const { trackReturnToApplication } = this.props
    const formattedPhoneNumber = formatToE164(
      this.state.failedSubmitData.phoneNumber,
      this.state.phoneCountry as CountryCode
    )
    trackReturnToApplication(
      DA.EventActions.Submit,
      DA.ReturnToApplication.ReclaimSMS
    )
    try {
      this.setState({ isSmsSendInProgress: true })
      await this.props.sendSmsRecovery(formattedPhoneNumber)
      trackReturnToApplication(
        DA.EventActions.Success,
        DA.ReturnToApplication.ReclaimSMS
      )
      this.setState({ hasSentSms: true, isSmsSendInProgress: false })
    } catch {
      this.setState({
        error: '',
        isSmsSendInProgress: false,
        showSmsPromptModal: false,
      })
      trackReturnToApplication(
        DA.EventActions.Error,
        DA.ReturnToApplication.ReclaimSMS
      )
    }
  }

  public handleSubmit = async (applicationData: {
    phoneNumber: string
    email: string
  }) => {
    const {
      onSubmit,
      checkEmailIsAssociatedWithDasher,
      trackReturnToApplication,
      applicantUniqueLink,
    } = this.props

    try {
      this.setState({ error: '' })
      const result = await onSubmit(applicationData)

      setApplicantStore({
        uniqueLink: result.uniqueLink,
        email: applicationData.email,
      })

      pageLoad(result.uniqueLink, Steps.ProfileSetup)
    } catch (error) {
      this.setState({ failedSubmitData: applicationData })
      if (isFeatureFlagEnabled(Exp.DX_R2A_SKIP_PHONE)) {
        trackReturnToApplication(
          DA.EventActions.Rendered,
          DA.ReturnToApplication.SkipPhone
        )
        try {
          const email = applicationData.email
          // Attempt API call to check email
          const {
            shouldForwardToLogin,
            applicantUniqueLink: apiUniqueLink,
          } = await checkEmailIsAssociatedWithDasher(email)

          /**
           * We set the unique link returned from the API in the case that
           * the applicant does not have a Dasher yet. This is because the
           * GET /v1/dasher_applicants/ only returns a unique link if both
           * email and phone number match. This is to support our
           * "R2A skip phone" feature where the phone does not have to match.
           **/
          if (apiUniqueLink || applicantUniqueLink) {
            SimpleStore.set(
              UNIQUE_LINK_KEY,
              apiUniqueLink || applicantUniqueLink
            )
          }

          if (shouldForwardToLogin) {
            trackReturnToApplication(
              DA.EventActions.Success,
              DA.ReturnToApplication.SkipPhone,
              {
                value: applicantUniqueLink,
              }
            )
            redirectToLogin(email)
          } else {
            trackReturnToApplication(
              DA.EventActions.Error,
              DA.ReturnToApplication.SkipPhone,
              {
                value: 'no-matching-dasher',
              }
            )
            if (isFeatureFlagEnabled(Exp.DX_R2A_SMS_RECLAIM)) {
              const { shouldTriggerSms: shouldTriggerSendSmsPrompt } = await shouldTriggerSms(applicationData.phoneNumber)
              this.setState({ showSmsPromptModal: shouldTriggerSendSmsPrompt })
            }
            this.handleError(error)
          }
        } catch {
          trackReturnToApplication(
            DA.EventActions.Error,
            DA.ReturnToApplication.SkipPhone,
            {
              value: 'api-error',
            }
          )
          // Display the original error, not error from checking the email
          this.handleError(error)
        }
      } else if (isFeatureFlagEnabled(Exp.DX_R2A_SMS_RECLAIM)) {
        const { shouldTriggerSms: shouldTriggerSendSmsPrompt } = await shouldTriggerSms(applicationData.phoneNumber)
        this.setState({ showSmsPromptModal: shouldTriggerSendSmsPrompt })
        this.handleError(error)
      } else {
        // Handle onSubmit error
        // If DX_R2A_SKIP_PHONE is in control
        this.handleError(error)
      }
    }
  }

  public handleReturnToApplicationLoggedIn(applicantUniqueLink) {
    const { trackDasherContinueApplication } = this.props
    trackDasherContinueApplication({
      action: DA.EventActions.Clicked,
      name: AlreadyStartedEnum.LoggedIn,
    })

    window.location.replace(routes.application.path + applicantUniqueLink)
  }

  public render() {
    const {
      alreadyStartedSigningUpModal,
      applicantUniqueLink,
      includeAllCountryCodes,
      phoneCountry,
      employmentType = 'INDIVIDUAL_CONTRACTOR',
    } = this.props

    const { error } = this.state
    const phoneCountryCodes = getOrderedPhoneCountryCodes({
      preferredCountryCodes: [this.state.phoneCountry, phoneCountry],
      includeAllCountries: includeAllCountryCodes,
    })

    if (
      alreadyStartedSigningUpModal.email ||
      alreadyStartedSigningUpModal.phoneNumber
    ) {
      StartedSigningUpModal.fields.email = alreadyStartedSigningUpModal.email
      StartedSigningUpModal.fields.phoneNumber = formatToE164(
        alreadyStartedSigningUpModal.phoneNumber,
        phoneCountry
      )
    }

    const type = alreadyStartedSigningUpModal.type
    const isW2 = employmentType === 'W2'

    let title
    let description
    switch (type) {
      case AlreadyStartedEnum.ExistingCredentials:
        title = t.h1Existing()
        description = t.descriptionExisting()
        break
      case AlreadyStartedEnum.LoggedIn:
        title = t.h1LoggedIn()
        description = t.descriptionLoggedIn()
        break
      default:
        title = isW2 ? t.h1W2() : t.h1()
        description = isW2 ? t.descriptionW2() : t.description()
    }

    if (
      isFeatureFlagEnabled(Exp.DX_R2A_SMS_RECLAIM) &&
      this.state.showSmsPromptModal
    ) {
      if (this.state.hasSentSms) {
        return (
          <SmsSentConfirmation
            onClose={this.closeSmsModal}
            onClick={this.handleSendSmsRecovery}
            isSending={this.state.isSmsSendInProgress}
            trackReturnToApplication={this.props.trackReturnToApplication}
            onBack={() => this.setState({ hasSentSms: false })}
          />
        )
      }
      return (
        <SmsSentPrompt
          onClose={this.closeSmsModal}
          onClick={this.handleSendSmsRecovery}
          isSending={this.state.isSmsSendInProgress}
          trackReturnToApplication={this.props.trackReturnToApplication}
          onBack={() => this.setState({ showSmsPromptModal: false })}
        />
      )
    }

    return (
      <Modal
        title={title}
        onClose={this.props.onClose}
        isFullScreenOnMobile={false}
      >
        <StackChildren>
          <Text>{description}</Text>
          <Form
            fields={StartedSigningUpModal.fields}
            validators={StartedSigningUpModal.validators}
            onSubmit={this.handleSubmit}
          >
            {(fields, handleChange, handleSubmit, { isLoading }) => (
              <form onSubmit={handleSubmit}>
                <StackChildren>
                  {type === AlreadyStartedEnum.LoggedIn &&
                  applicantUniqueLink ? (
                    <Button
                      tag="a"
                      data-anchor-id="resume-app"
                      onClick={() =>
                        this.handleReturnToApplicationLoggedIn(
                          applicantUniqueLink
                        )
                      }
                    >
                      {t.resumeApplicationPrimaryAction()}
                    </Button>
                  ) : (
                    <React.Fragment>
                      <EmailField
                        type="email"
                        placeholder={t.email()}
                        autoComplete="email"
                        onChange={val => handleChange('email', val)}
                        label="Email"
                        isLabelHidden
                        autoFocus
                        {...fields.email}
                      />
                      <PhoneField
                        isInternationalEnabled
                        type="tel"
                        placeholder={t.phone()}
                        onChange={(unformatted, formatted) => {
                          this.setState({
                            nationalPhoneNumber: unformatted,
                          })
                          handleChange('phoneNumber', formatted.number.number)
                        }}
                        onCountryChange={(
                          countrySelected,
                          unformatted,
                          formatted
                        ) => {
                          this.setState({
                            phoneCountry: countrySelected,
                            nationalPhoneNumber: unformatted,
                          })
                          handleChange('phoneNumber', formatted.number.number)
                        }}
                        autoComplete="tel"
                        label="Phone Number"
                        isLabelHidden
                        value={
                          this.state.nationalPhoneNumber ||
                          fields.phoneNumber.value
                        }
                        currentCountry={this.state.phoneCountry || phoneCountry}
                        error={fields.phoneNumber.error}
                        availableCountries={phoneCountryCodes}
                        autoDetectCountry={true}
                      />
                      {error && (
                        <Error isSquished>
                          <Text size={Text.Sizes.S12}>{error}</Text>
                        </Error>
                      )}
                      <Button
                        onClick={async () => {
                          try {
                            await handleSubmit()
                          } catch (e) {
                            console.warn(e)
                          }
                        }}
                        state={
                          isLoading
                            ? Button.States.Loading
                            : Button.States.Default
                        }
                      >
                        {t.continue()}
                      </Button>
                    </React.Fragment>
                  )}
                </StackChildren>
              </form>
            )}
          </Form>
        </StackChildren>
      </Modal>
    )
  }
}

const mapStateToProps = state => ({
  alreadyStartedSigningUpModal: state.alreadyStartedSigningUpModal,
  applicantUniqueLink: state.applicantUniqueLink,
  experiments: state.experiments,
})

const mapDispatchToProps = {
  trackDasherContinueApplication: events.trackDasherContinueApplication,
  onSubmit: actions.findDasherApplication,
  checkEmailIsAssociatedWithDasher: actions.checkEmailIsAssociatedWithDasher,
  sendSmsRecovery: actions.sendSmsRecovery,
  trackReturnToApplication: events.trackReturnToApplication,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(StartedSigningUpModal)
